import { colorSystem, Text as DesignSystemText, Flexbox, Tooltip } from '@luminovo/design-system';
import { Bar, BarChart, Rectangle, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { getDefaultKeyColor } from '../../color/getDefaultColor';

export type SingleHorizontalStackedBarDatum<TKeys extends string> = {
    [key in TKeys]?: number;
} & { label: string };

export type SingleHorizontalStackedBarChartProps<
    TKeys extends string,
    T extends SingleHorizontalStackedBarDatum<TKeys>,
> = {
    /** One bar per datum, each split into a segment per key. */
    data: T[];
    keys: TKeys[];
    formatKey: (key: TKeys) => string;
    getColor?: (key: TKeys) => string;
    getTooltip?: (key: TKeys) => string | undefined;
    getKeyCount?: (key: TKeys) => number;
    /** Fixed width in pixels. Without it the chart fills its container. */
    width?: number;
};

const ROW_HEIGHT = 24;
const BAR_HEIGHT = 12;
// Space after every segment, so neighbours stand apart.
const SEGMENT_GAP = 4;

export function SingleHorizontalStackedBarChart<
    TKeys extends string,
    T extends SingleHorizontalStackedBarDatum<TKeys>,
>({
    data,
    keys,
    formatKey,
    getColor = getDefaultKeyColor(keys),
    getTooltip,
    getKeyCount,
    width,
}: SingleHorizontalStackedBarChartProps<TKeys, T>): JSX.Element {
    return (
        <Flexbox gap="4px" sx={{ flexDirection: 'column', width: width ?? '100%' }}>
            <ResponsiveContainer width="100%" height={data.length * ROW_HEIGHT}>
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    barCategoryGap={0}
                    // The bars have no tooltip, so a tab stop on the chart would lead nowhere.
                    accessibilityLayer={false}
                >
                    <XAxis type="number" hide domain={[0, 'dataMax']} />
                    <YAxis type="category" dataKey="label" hide />
                    {keys.map((key) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            stackId="stack"
                            fill={getColor(key)}
                            barSize={BAR_HEIGHT}
                            isAnimationActive={false}
                            shape={(props) => (
                                <Rectangle
                                    {...props}
                                    width={Math.max((props.width ?? 0) - SEGMENT_GAP, 0)}
                                    radius={2}
                                />
                            )}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            {keys.length > 1 && (
                <Legend
                    keys={keys}
                    formatKey={formatKey}
                    getColor={getColor}
                    getTooltip={getTooltip}
                    getKeyCount={getKeyCount}
                />
            )}
        </Flexbox>
    );
}

function Legend<TKeys extends string>({
    keys,
    formatKey,
    getColor,
    getTooltip = () => undefined,
    getKeyCount,
}: {
    keys: TKeys[];
    formatKey: (key: TKeys) => string;
    getColor: (key: TKeys) => string;
    getTooltip?: (key: TKeys) => string | undefined;
    getKeyCount?: (key: TKeys) => number;
}): JSX.Element {
    return (
        <Flexbox gap="24px" sx={{ justifyContent: 'space-between' }}>
            {keys.map((key) => (
                <Tooltip title={getTooltip(key) ?? ''} key={key} placement="top">
                    <Flexbox gap="4px" sx={{ alignItems: 'center', paddingRight: '4px' }}>
                        <div style={{ borderRadius: 2, width: 8, height: 8, backgroundColor: getColor(key) }} />
                        <DesignSystemText color={colorSystem.neutral[6]}>
                            {formatKey(key)}{' '}
                            {getKeyCount && <span style={{ color: colorSystem.neutral[9] }}>{getKeyCount(key)}</span>}
                        </DesignSystemText>
                    </Flexbox>
                </Tooltip>
            ))}
        </Flexbox>
    );
}
